import { queryOptions } from '@tanstack/react-query';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { mparticleIntegrationKeys, parseMParticleIntegration, type MParticleIntegration } from './entity';

/**
 * mParticle connector reads.
 *
 * `staleTime: 0` — the record is mutated by its own config page and read back by
 * the integrations directory and connections overview, so a revisit must never
 * serve a stale copy. Ember used `store.queryRecord` for the same reason.
 */

const STALE_TIME = 0;

/**
 * @public The connector's config, or `null` when it has never been connected.
 *
 * **A 404 is the "not connected" answer, not a failure** — the backend has no
 * record until the connector is first connected, which is what puts the page in
 * its connect state. Every other status is a real error and propagates: the page
 * must be able to tell "not connected" from "we could not load it", or a 5xx
 * would render the connect form and a submit would 409 against a record that
 * already exists.
 */
export async function getMParticleIntegration(workspaceId: number): Promise<MParticleIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getMparticle({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.mparticle == null ? null : parseMParticleIntegration(data.mparticle);
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** @public */
export const getMParticleIntegrationQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: mparticleIntegrationKeys.detail(workspaceId, 'mparticle'),
    queryFn: () => getMParticleIntegration(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * @public The same read, for the status fan-outs (integrations directory,
 * connections overview) rather than the config page.
 *
 * Identical `queryKey` **and** `queryFn` — one cache slot and one fetch, shared
 * with the config page — differing only in `retry: false`. Keep it that way: two
 * option factories on one key that disagree about how to *fetch* resolve to
 * whichever observer registered last.
 *
 * The fan-outs fold every connector's `isLoading` into a single page-level
 * loader, so a 5xx running the default three-attempt backoff would hold the
 * whole Connections tab on a spinner over one optional record. Failing fast
 * leaves `data` undefined, which those fan-outs already read as "not enabled".
 * The config page keeps retrying, since there the record *is* the page.
 */
export const getMParticleIntegrationStatusQueryOptions = (workspaceId: number) =>
  queryOptions({
    ...getMParticleIntegrationQueryOptions(workspaceId),
    retry: false,
  });
